import { useState, useEffect, useCallback, useMemo } from 'react';
import { s, authHeaders, Toast, StandardDataGrid, gridActionButton } from './listPageStyles';

const API_BASE = '/api/b2b-returns';

const STATUS_META = {
  0: { label: 'Beklemede', tone: 'warn' },
  1: { label: 'Siparişe Çevrildi', tone: 'ok' },
  2: { label: 'Yanlış İade', tone: 'bad' },
};

const STATUS_TABS = [
  { key: 'all', label: 'Tümü' },
  { key: 0, label: 'Beklemede' },
  { key: 1, label: 'Çevrilen' },
  { key: 2, label: 'Reddedilen' },
];

const PAGE_SIZE_DEFAULT = 20;

function pick(obj, ...keys) {
  if (!obj) return undefined;
  for (const k of keys) {
    if (obj[k] !== undefined && obj[k] !== null) return obj[k];
  }
  return undefined;
}

function unwrapResponse(payload) {
  const data = pick(payload, 'Data', 'data') ?? payload;
  const items = pick(data, 'items', 'Items') ?? [];
  const pendingCount = pick(data, 'pendingCount', 'PendingCount') ?? 0;
  return { items, pendingCount };
}

function statusPillFor(status) {
  const meta = STATUS_META[status] || { label: String(status ?? '-'), tone: 'neutral' };
  const tones = {
    ok: { color: '#0f5132', background: 'rgba(0, 200, 117, 0.12)', borderColor: 'rgba(0, 200, 117, 0.30)' },
    warn: { color: '#92400e', background: 'rgba(253, 171, 61, 0.14)', borderColor: 'rgba(253, 171, 61, 0.32)' },
    bad: { color: '#7f1d1d', background: 'rgba(226, 68, 92, 0.12)', borderColor: 'rgba(226, 68, 92, 0.30)' },
    neutral: { color: '#64748b', background: 'rgba(100, 116, 139, 0.14)', borderColor: 'rgba(100, 116, 139, 0.28)' },
  };
  const t = tones[meta.tone];
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center', gap: 6,
      padding: '4px 10px', borderRadius: 999, fontWeight: 800, fontSize: 12,
      border: `1px solid ${t.borderColor}`, background: t.background, color: t.color,
      whiteSpace: 'nowrap',
    }}>
      <span style={{ width: 7, height: 7, borderRadius: 999, background: 'currentColor', opacity: 0.85 }} />
      {meta.label}
    </span>
  );
}

function formatDate(value) {
  if (!value) return '-';
  try {
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return '-';
    return d.toLocaleString('tr-TR', {
      day: '2-digit', month: '2-digit', year: 'numeric',
      hour: '2-digit', minute: '2-digit',
    });
  } catch {
    return '-';
  }
}

function formatCurrency(value) {
  const n = Number(value);
  if (!Number.isFinite(n)) return '-';
  return n.toLocaleString('tr-TR', { style: 'currency', currency: 'TRY', minimumFractionDigits: 2 });
}

export function B2bReturnsPageLegacy() {
  const [statusFilter, setStatusFilter] = useState('all');
  const [searchText, setSearchText] = useState('');
  const [rows, setRows] = useState([]);
  const [pendingCount, setPendingCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [toast, setToast] = useState(null);

  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(PAGE_SIZE_DEFAULT);
  const [sortField, setSortField] = useState('ReceivedAt');
  const [sortDir, setSortDir] = useState('desc');
  const [selectedId, setSelectedId] = useState(null);

  const [detailRow, setDetailRow] = useState(null);
  const [actionBusyId, setActionBusyId] = useState(null);

  const showToast = useCallback((type, message) => {
    setToast({ type, message });
    window.setTimeout(() => setToast(null), 3000);
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(API_BASE, { headers: authHeaders() });
      if (!res.ok) throw new Error(`Liste alınamadı (${res.status})`);
      const payload = await res.json();
      const { items, pendingCount: pc } = unwrapResponse(payload);
      setRows(Array.isArray(items) ? items : []);
      setPendingCount(pc);
      setPage(1);
    } catch (err) {
      showToast('error', err.message || 'Liste alınamadı');
      setRows([]);
      setPendingCount(0);
    } finally {
      setLoading(false);
    }
  }, [showToast]);

  useEffect(() => { load(); }, [load]);

  const filteredSorted = useMemo(() => {
    const needle = searchText.trim().toLocaleLowerCase('tr-TR');
    const match = (row) => {
      if (statusFilter !== 'all' && pick(row, 'status', 'Status') !== statusFilter) return false;
      if (!needle) return true;
      const fields = [
        pick(row, 'pointName', 'PointName'),
        pick(row, 'pointCode', 'PointCode'),
        pick(row, 'returnNumber', 'ReturnNumber'),
        pick(row, 'source', 'Source'),
      ];
      return fields.some(v => v && String(v).toLocaleLowerCase('tr-TR').includes(needle));
    };

    const filtered = rows.filter(match);

    const dir = sortDir === 'desc' ? -1 : 1;
    const sortKey = (row) => {
      switch (sortField) {
        case 'PointName': return pick(row, 'pointName', 'PointName') || '';
        case 'ReturnNumber': return pick(row, 'returnNumber', 'ReturnNumber') || '';
        case 'Amount': return Number(pick(row, 'amount', 'Amount')) || 0;
        case 'ItemCount': return Number(pick(row, 'itemCount', 'ItemCount')) || 0;
        case 'Status': return Number(pick(row, 'status', 'Status')) || 0;
        case 'Source': return pick(row, 'source', 'Source') || '';
        case 'ReceivedAt':
        default: {
          const v = pick(row, 'receivedAt', 'ReceivedAt');
          return v ? new Date(v).getTime() : 0;
        }
      }
    };

    return [...filtered].sort((a, b) => {
      const av = sortKey(a);
      const bv = sortKey(b);
      if (av < bv) return -1 * dir;
      if (av > bv) return 1 * dir;
      return 0;
    });
  }, [rows, searchText, statusFilter, sortField, sortDir]);

  const pagedRows = useMemo(() => {
    const start = (page - 1) * pageSize;
    return filteredSorted.slice(start, start + pageSize);
  }, [filteredSorted, page, pageSize]);

  const performAction = async (row, action, successMessage) => {
    const id = pick(row, 'id', 'Id');
    if (!id) return;
    setActionBusyId(id);
    try {
      const res = await fetch(`${API_BASE}/${id}/${action}`, {
        method: 'POST',
        headers: authHeaders(),
      });
      const payload = await res.json().catch(() => null);
      if (!res.ok) {
        const msg = pick(payload, 'Message', 'message') || `İşlem başarısız (${res.status})`;
        throw new Error(msg);
      }
      showToast('success', successMessage);
      await load();
    } catch (err) {
      showToast('error', err.message || 'İşlem başarısız');
    } finally {
      setActionBusyId(null);
    }
  };

  const columns = useMemo(() => [
    {
      field: 'returnNumber',
      headerName: 'İade No',
      flex: 0.9,
      minWidth: 140,
      valueGetter: ({ row }) => pick(row, 'returnNumber', 'ReturnNumber') || '-',
    },
    {
      field: 'pointName',
      headerName: 'Nokta',
      flex: 1.4,
      minWidth: 200,
      renderCell: ({ row }) => {
        const name = pick(row, 'pointName', 'PointName') || '-';
        const code = pick(row, 'pointCode', 'PointCode');
        return (
          <div style={{ display: 'flex', flexDirection: 'column', lineHeight: 1.3 }}>
            <span style={{ fontWeight: 700 }}>{name}</span>
            {code && <span style={{ fontSize: 12, color: '#6b7280' }}>{code}</span>}
          </div>
        );
      },
    },
    {
      field: 'amount',
      headerName: 'Tutar',
      flex: 0.8,
      minWidth: 130,
      valueGetter: ({ row }) => formatCurrency(pick(row, 'amount', 'Amount')),
    },
    {
      field: 'itemCount',
      headerName: 'Kalem',
      flex: 0.5,
      minWidth: 80,
      valueGetter: ({ row }) => pick(row, 'itemCount', 'ItemCount') ?? '-',
    },
    {
      field: 'source',
      headerName: 'Kanal',
      flex: 0.7,
      minWidth: 110,
      valueGetter: ({ row }) => pick(row, 'source', 'Source') || '-',
    },
    {
      field: 'status',
      headerName: 'Durum',
      flex: 0.8,
      minWidth: 160,
      renderCell: ({ row }) => statusPillFor(pick(row, 'status', 'Status')),
    },
    {
      field: 'receivedAt',
      headerName: 'Geliş Tarihi',
      flex: 0.9,
      minWidth: 150,
      valueGetter: ({ row }) => formatDate(pick(row, 'receivedAt', 'ReceivedAt')),
    },
    {
      field: 'actions',
      headerName: 'Aksiyon',
      flex: 1.4,
      minWidth: 280,
      sortable: false,
      filterable: false,
      renderCell: ({ row }) => {
        const id = pick(row, 'id', 'Id');
        const status = pick(row, 'status', 'Status');
        const busy = actionBusyId === id;
        return (
          <div style={{ display: 'flex', gap: 8 }} onClick={(e) => e.stopPropagation()}>
            <button
              type="button"
              style={{ ...gridActionButton, background: 'var(--accent)' }}
              onClick={() => setDetailRow(row)}
            >
              Detay
            </button>
            {status === 0 && (
              <>
                <button
                  type="button"
                  style={{ ...gridActionButton, background: '#16a34a', opacity: busy ? 0.6 : 1 }}
                  disabled={busy}
                  onClick={() => performAction(row, 'convert', 'Siparişe çevrildi olarak işaretlendi')}
                  title="Bu iade kaydını sipariş olarak işaretle"
                >
                  {busy ? '...' : 'Çevrildi'}
                </button>
                <button
                  type="button"
                  style={{ ...gridActionButton, background: '#dc2626', opacity: busy ? 0.6 : 1 }}
                  disabled={busy}
                  onClick={() => performAction(row, 'dismiss', 'Yanlış iade olarak işaretlendi')}
                  title="Yanlış iade olarak işaretle"
                >
                  {busy ? '...' : 'Yanlış'}
                </button>
              </>
            )}
          </div>
        );
      },
    },
  ], [actionBusyId]);

  const totalCount = filteredSorted.length;

  return (
    <div style={s.container}>
      <div style={s.pageHeader}>
        <div style={s.pageHeaderText}>
          <h1 style={s.pageTitle}>B2B İadeler</h1>
          <p style={s.pageSubtitle}>
            B2B sistemi ve OT portalından gelen iade kayıtları. Saha temsilcisi mobile'dan
            siparişe çevirir veya yanlış iade olarak işaretler.
          </p>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <span style={{
            display: 'inline-flex', alignItems: 'center', gap: 8,
            padding: '8px 14px', borderRadius: 999,
            background: 'rgba(253, 171, 61, 0.14)',
            border: '1px solid rgba(253, 171, 61, 0.32)',
            color: '#92400e', fontWeight: 800, fontSize: 13,
          }}>
            <span style={{ width: 8, height: 8, borderRadius: 999, background: 'var(--warn)' }} />
            {pendingCount} bekleyen
          </span>
          <button type="button" style={s.btnSecondary} onClick={load} disabled={loading}>
            {loading ? 'Yükleniyor...' : 'Yenile'}
          </button>
        </div>
      </div>

      <div style={s.toolbar}>
        <div style={s.toolbarLeft}>
          {STATUS_TABS.map(tab => {
            const active = statusFilter === tab.key;
            return (
              <button
                key={String(tab.key)}
                type="button"
                onClick={() => { setStatusFilter(tab.key); setPage(1); }}
                style={{
                  ...(active ? s.btnPrimary : s.btnSecondary),
                  padding: '6px 14px',
                  fontSize: 13,
                }}
              >
                {tab.label}
              </button>
            );
          })}
        </div>
        <div style={s.toolbarRight}>
          <input
            type="search"
            value={searchText}
            onChange={(e) => { setSearchText(e.target.value); setPage(1); }}
            placeholder="Nokta / iade no / kanal..."
            style={s.filterInput}
          />
        </div>
      </div>

      <StandardDataGrid
        rows={pagedRows}
        columns={columns}
        totalCount={totalCount}
        loading={loading}
        pageSize={pageSize}
        currentPage={page}
        onPageChange={setPage}
        onPageSizeChange={(n) => { setPageSize(n); setPage(1); }}
        sortField={sortField}
        sortDir={sortDir}
        onSortChange={(field, dir) => { setSortField(field); setSortDir(dir); }}
        selectedId={selectedId}
        onRowClick={(row) => setSelectedId(pick(row, 'id', 'Id'))}
        onRowDoubleClick={(row) => setDetailRow(row)}
        exportFileName="b2b-iadeler"
      />

      {detailRow && (
        <div style={s.overlay} onClick={() => setDetailRow(null)}>
          <div style={s.modal} onClick={(e) => e.stopPropagation()}>
            <div style={s.modalHeader}>
              <h2 style={s.modalTitle}>
                {pick(detailRow, 'returnNumber', 'ReturnNumber') || 'İade Detayı'}
              </h2>
              <button type="button" style={s.closeBtn} onClick={() => setDetailRow(null)}>×</button>
            </div>
            <div style={s.modalBody}>
              <div style={s.formGroup}>
                <label style={s.label}>Nokta</label>
                <div style={s.inputView}>
                  {pick(detailRow, 'pointName', 'PointName') || '-'}
                  {pick(detailRow, 'pointCode', 'PointCode') && (
                    <span style={{ color: '#6b7280', marginLeft: 8 }}>
                      ({pick(detailRow, 'pointCode', 'PointCode')})
                    </span>
                  )}
                </div>
              </div>
              <div style={s.formRow}>
                <div style={s.formGroup}>
                  <label style={s.label}>Tutar</label>
                  <div style={s.inputView}>{formatCurrency(pick(detailRow, 'amount', 'Amount'))}</div>
                </div>
                <div style={s.formGroup}>
                  <label style={s.label}>Kalem Sayısı</label>
                  <div style={s.inputView}>{pick(detailRow, 'itemCount', 'ItemCount') ?? '-'}</div>
                </div>
              </div>
              <div style={s.formRow}>
                <div style={s.formGroup}>
                  <label style={s.label}>Kanal</label>
                  <div style={s.inputView}>{pick(detailRow, 'source', 'Source') || '-'}</div>
                </div>
                <div style={s.formGroup}>
                  <label style={s.label}>Durum</label>
                  <div>{statusPillFor(pick(detailRow, 'status', 'Status'))}</div>
                </div>
              </div>
              <div style={s.formGroup}>
                <label style={s.label}>Geliş Tarihi</label>
                <div style={s.inputView}>{formatDate(pick(detailRow, 'receivedAt', 'ReceivedAt'))}</div>
              </div>
            </div>
            <div style={s.modalFooter}>
              {pick(detailRow, 'status', 'Status') === 0 && (
                <>
                  <button
                    type="button"
                    style={s.btnDanger}
                    onClick={() => {
                      const r = detailRow; setDetailRow(null);
                      performAction(r, 'dismiss', 'Yanlış iade olarak işaretlendi');
                    }}
                  >
                    Yanlış İade
                  </button>
                  <button
                    type="button"
                    style={{ ...s.btnPrimary, background: '#16a34a' }}
                    onClick={() => {
                      const r = detailRow; setDetailRow(null);
                      performAction(r, 'convert', 'Siparişe çevrildi olarak işaretlendi');
                    }}
                  >
                    Siparişe Çevrildi
                  </button>
                </>
              )}
              <button type="button" style={s.btnSecondary} onClick={() => setDetailRow(null)}>
                Kapat
              </button>
            </div>
          </div>
        </div>
      )}

      <Toast toast={toast} />
    </div>
  );
}

export default B2bReturnsPageLegacy;
